import type { Column } from '@/components/Organisms/DataTable/DataTable.types';
import { exportDownloadTimestamp } from '@/utils/exportDownloadTimestamp';

const escapeCSV = (value: string) => {
	let cleanValue = String(value || '')
		.replace(/\r/g, '')
		.replace(/\n/g, ' ')
		.trim();

	if (
		cleanValue.includes(',') ||
		cleanValue.includes('"') ||
		cleanValue !== cleanValue.trim()
	) {
		cleanValue = cleanValue.replace(/"/g, '""');
		return `"${cleanValue}"`;
	}

	return cleanValue;
};

const processRow = (row: any, column: Column, all: boolean = false) => {
	const cellData = row[column.key];
	if (all) {
		return escapeCSV(cellData);
	}
	if (column.type === 'string-status') {
		if (cellData.props?.children) {
			return escapeCSV(cellData.props?.children.props.text);
		}
		if (cellData.props?.text) {
			return escapeCSV(cellData.props.text);
		}
		return escapeCSV(cellData);
	}
	if (cellData?.props?.text) {
		return escapeCSV(cellData.props.text);
	}

	if (cellData?.props?.children) {
		return cellData.props.children;
	}
	return escapeCSV(cellData);
};

export const generateCSVFromTable = async (
	columns: Column[],
	items: any[],
	docName: string,
	all: boolean = false,
) => {
	try {
		const exportColumns = columns.filter((col) => !col.skipExport);

		const headers = exportColumns.map((col) => escapeCSV(col.label)).join(',');

		const CHUNK_SIZE = 5000;
		const blobParts: BlobPart[] = [];

		blobParts.push(headers + '\n');

		const processRowData = (row: any) =>
			exportColumns.map((col: Column) => processRow(row, col, all)).join(',');

		for (let i = 0; i < items.length; i += CHUNK_SIZE) {
			const chunk = items.slice(i, i + CHUNK_SIZE);

			let chunkStr = '';

			for (const row of chunk) {
				chunkStr += processRowData(row) + '\n';

				if (row.subRows?.length) {
					for (const subRow of row.subRows) {
						chunkStr += processRowData(subRow) + '\n';
					}
				}
			}

			blobParts.push(chunkStr);

			await new Promise((resolve) => setTimeout(resolve, 0));
		}

		const blob = new Blob(blobParts, { type: 'text/csv;charset=utf-8;' });

		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${docName}-${exportDownloadTimestamp()}.csv`;
		link.click();

		URL.revokeObjectURL(url);
	} catch (error) {
		console.error('CSV generation failed:', error);
	}
};

const processExcelCell = (row: any, column: Column, all: boolean = false) => {
	const cellData = row[column.key];
	if (all) {
		return cellData || '';
	}
	if (column.type === 'string-status') {
		if (cellData?.props?.children) {
			return cellData.props?.children.props.text || '';
		}
		if (cellData?.props?.text) {
			return cellData.props.text || '';
		}
		return cellData || '';
	}
	if (cellData?.props?.text) {
		return cellData.props.text;
	}
	if (cellData?.props?.children) {
		return cellData.props.children;
	}
	return cellData || '';
};

export const generateExcelFromTable = async (
	columns: any[],
	items: any[],
	docName: string,
	all: boolean = false,
) => {
	try {
		const XLSX = await import('xlsx');

		const exportColumns = columns.filter((col) => !col.skipExport);
		const headers = exportColumns.map((col: any) => col.label);

		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet([headers]);

		const CHUNK_SIZE = 5000;
		let rowIndex = 1;

		const colWidths = headers.map((h: string) => h.length);

		const processRow = (row: any) =>
			exportColumns.map((col: any, colIndex: number) => {
				const value = processExcelCell(row, col, all) ?? '';
				const strVal = String(value);

				colWidths[colIndex] = Math.max(colWidths[colIndex], strVal.length);

				return strVal;
			});

		for (let i = 0; i < items.length; i += CHUNK_SIZE) {
			const chunk = items.slice(i, i + CHUNK_SIZE);

			const chunkRows: any[] = [];

			for (const row of chunk) {
				chunkRows.push(processRow(row));

				if (row.subRows?.length) {
					for (const subRow of row.subRows) {
						chunkRows.push(processRow(subRow));
					}
				}
			}

			XLSX.utils.sheet_add_aoa(worksheet, chunkRows, {
				origin: { r: rowIndex, c: 0 },
			});

			rowIndex += chunkRows.length;

			await new Promise((resolve) => setTimeout(resolve, 0));
		}

		worksheet['!cols'] = colWidths.map((w) => ({
			wch: Math.min(w + 2, 50),
		}));

		XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

		XLSX.writeFile(workbook, `${docName}-${exportDownloadTimestamp()}.xlsx`);
	} catch (error) {
		console.error('Failed to generate Excel:', error);
	}
};
